import { ConversationDetail, ConversationFilters, ConversationSummary, FAQ } from './types';

// Read the backend URL from the runtime config (public/config.js),
// just like the main frontend does. Falls back to '' for local dev.
const cfg = (window as any)._adminEnv_ || {};
const BACKEND = (cfg.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const BASE = `${BACKEND}/api`;

function headers(token?: string) {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

export async function adminLogin(name: string, phone: string) {
  const res = await fetch(`${BASE}/admin/login`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ name, phone }),
  });
  if (!res.ok) throw new Error('Login failed');
  return res.json();
}

export async function fetchConversations(
  filters: ConversationFilters = {},
  token?: string
): Promise<ConversationSummary[]> {
  const params = new URLSearchParams();
  if (filters.agency && filters.agency !== 'all') params.set('agency', filters.agency);
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.language && filters.language !== 'all') params.set('language', filters.language);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.human_only) params.set('human_only', 'true');

  const res = await fetch(`${BASE}/admin/conversations?${params}`, { headers: headers(token) });
  if (!res.ok) throw new Error('Failed to fetch conversations');
  const data = await res.json();
  // Backend wraps list under data.conversations
  return Array.isArray(data) ? data : (data.conversations ?? []);
}

export async function fetchConversation(
  id: string,
  token?: string
): Promise<ConversationDetail> {
  const res = await fetch(`${BASE}/admin/conversations/${id}`, { headers: headers(token) });
  if (!res.ok) throw new Error('Failed to fetch conversation');
  const data = await res.json();
  // Backend wraps detail under data.conversation
  return data.conversation ?? data;
}

export async function updateConversationStatus(
  id: string,
  status: string,
  token?: string
) {
  const res = await fetch(`${BASE}/conversations/${id}/update`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update conversation');
  return res.json();
}

export async function fetchKnowledgeBase(token?: string): Promise<FAQ[]> {
  const res = await fetch(`${BASE}/admin/knowledge-base`, { headers: headers(token) });
  if (!res.ok) throw new Error('Failed to fetch knowledge base');
  const data = await res.json();
  // Backend returns { success, items: [...], count }
  return Array.isArray(data) ? data : (data.items ?? data.faqs ?? []);
}

export async function createFAQ(faq: Partial<FAQ>, token?: string): Promise<FAQ> {
  const res = await fetch(`${BASE}/admin/knowledge-base`, {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(faq),
  });
  if (!res.ok) throw new Error('Failed to create FAQ');
  const data = await res.json();
  return data.item ?? data.faq ?? data;
}

export async function updateFAQ(id: string, faq: Partial<FAQ>, token?: string): Promise<FAQ> {
  const res = await fetch(`${BASE}/admin/knowledge-base/${id}`, {
    method: 'PUT',
    headers: headers(token),
    body: JSON.stringify(faq),
  });
  if (!res.ok) throw new Error('Failed to update FAQ');
  const data = await res.json();
  return data.item ?? data.faq ?? data;
}

export async function deleteFAQ(id: string, token?: string): Promise<void> {
  const res = await fetch(`${BASE}/admin/knowledge-base/${id}`, {
    method: 'DELETE',
    headers: headers(token),
  });
  if (!res.ok) throw new Error('Failed to delete FAQ');
}

export async function reindexKnowledgeBase(token?: string) {
  const res = await fetch(`${BASE}/admin/knowledge-base/reindex`, {
    method: 'POST',
    headers: headers(token),
  });
  if (!res.ok) throw new Error('Failed to reindex');
  return res.json();
}
